import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Box, Card, CardContent, CardHeader, IconButton, ListItemIcon, Menu, MenuItem, Skeleton, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import Decimal from 'decimal.js';

const InvoiceBillingItemsCard = ({ onPartsValueChange, onWorksValueChange, worksDetails, onRemove, billingItemsCardError, onLoadingChange }) => {
    const [loading, setLoading] = useState(true);
    const [partsValue, setPartsValue] = useState(0);
    const [worksValue, setWorksValue] = useState(0);
    const [menuCardEl, setMenuCardEl] = useState(null);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (worksDetails && !initialized) {
            setPartsValue(Number(worksDetails.subtotal_parts_price || 0));
            setWorksValue(Number(worksDetails.subtotal_services_price || 0));
            setInitialized(true);
        }
    }, [worksDetails, initialized]);

    useEffect(() => {
        if (!initialized || !worksDetails) return;

        setLoading(false);
        onLoadingChange?.(worksDetails.id, false);
    }, [initialized, worksDetails, onLoadingChange]);

    const totalValue = new Decimal(partsValue).plus(worksValue);
    const openMenu = Boolean(menuCardEl);

    const handleClickMenu = (event) => {
        setMenuCardEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuCardEl(null);
    };

    useEffect(() => {
        onPartsValueChange?.(partsValue);
        onWorksValueChange?.(worksValue);
    }, [partsValue, worksValue, onPartsValueChange, onWorksValueChange]);

    const workTitle = worksDetails?.title || '';
    const formatBRL = (value) => {
        const number = new Decimal(value).toFixed(2);
        const parts = number.split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const decimalPart = parts[1];
        return `${integerPart},${decimalPart}`;
    };

    return (
        <Box sx={{ mb: 2 }}>
            <Card
                sx={{
                    backgroundColor: billingItemsCardError ? '#fdf5f5' : 'background.paper',
                }}
            >
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '1rem' }}>
                            <Typography variant="h5">{workTitle}</Typography>
                            <Tooltip title={loading ? "Aguardando carregamento" : ""}>
                                <span>
                                    <IconButton
                                        aria-label="more"
                                        onClick={handleClickMenu}
                                        aria-controls={openMenu ? 'long-menu' : undefined}
                                        aria-expanded={openMenu ? 'true' : undefined}
                                        aria-haspopup="true"
                                        disabled={loading}
                                        sx={{
                                            border: 'none',
                                            '&:hover': { backgroundColor: 'transparent' },
                                            '&:focus': { outline: 'none' },
                                        }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Menu
                                id="long-menu"
                                anchorEl={menuCardEl}
                                open={openMenu}
                                onClose={handleCloseMenu}
                                MenuListProps={{
                                    'aria-labelledby': 'long-button',
                                }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        handleCloseMenu();
                                        onRemove?.();
                                    }}
                                >
                                    <ListItemIcon>
                                        <CloseRoundedIcon fontSize="medium" />
                                    </ListItemIcon>
                                    <Typography variant="body1">Remover da fatura</Typography>
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        handleCloseMenu();
                                        if (worksDetails?.id) {
                                            window.open(`/works/${worksDetails.id}`, '_blank');
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <VisibilityRoundedIcon fontSize="medium" />
                                    </ListItemIcon>
                                    <Typography variant="body1">Ver detalhes</Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    }
                />
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Box sx={{ p: '1rem' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">Peças e Materiais </Typography>
                            {loading ? (
                                <Skeleton variant="h3" width={60} />
                            ) : (
                                <Typography variant="body1">R$ {formatBRL(partsValue)}</Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body1">Serviços</Typography>
                            {loading ? (
                                <Skeleton variant="h3" width={60} />
                            ) : (
                                <Typography variant="body1">
                                    R$ {formatBRL(worksValue)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', p: '1rem' }}>
                        <Typography variant="h5" sx={{ color: 'primary.main' }}>Total da atividade</Typography>
                        {loading ? (
                            <Skeleton variant="h3" width={60} />
                        ) : (
                            <Typography variant="h5" sx={{ color: 'primary.main' }}>R$ {formatBRL(totalValue)}</Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Box>
    )
}

export default InvoiceBillingItemsCard;